import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiSmartphone } from 'react-icons/fi';
import '../styles/login.css';
import { loginUser, verifyOtp, refreshAccessToken } from '../services/authService';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import { fetchBasicCandidateInfo } from '../services/candidate/api';


const Login = () => {
  const [step, setStep] = useState(1); // 1: credentials, 2: OTP
  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [emailForOtp, setEmailForOtp] = useState(''); // Store email after login attempt
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [resendEnabled, setResendEnabled] = useState(false);
const [loadingText, setLoadingText] = useState('');
  const navigate = useNavigate();
 
  const fetchBasicInfo = async () => {
    try {
      const response = await fetchBasicCandidateInfo();
      const data = response.data.data;
  
  
      if (!data) return;
  
     if (data.bgcStatus === 'PENDING_SUBMISSION'){
      navigate('/candidate/')
     }else navigate('/candidate/candidate-dashboard');
  
    } catch (error) {
      console.error('Error fetching basic info:', error);
    }
  };
 
  useEffect(() => {
    document.body.classList.add('login-page');
    return () => {
      document.body.classList.remove('login-page');
    };
  }, []);
 
  useEffect(() => {
    let activityTimeout;
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('bgc_token');
      const refreshToken = localStorage.getItem('bgc_refresh_token');
      if (token && refreshToken) {
        try {
          const decodedToken = jwtDecode(token);
          const currentTime = Date.now() / 1000;
          if (decodedToken.exp - currentTime < 300) {
            const response = await refreshAccessToken(refreshToken);
            localStorage.setItem('bgc_token', response.accessToken);
          }
        } catch (err) {
          localStorage.clear();
          navigate('/');
        }
      }
    };
 

    const resetActivityTimeout = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => {}, 15 * 60 * 1000);
    };
 
    const interval = setInterval(() => {
      if (document.hasFocus()) checkAndRefreshToken();
    }, 2 * 60 * 1000);
 
    window.addEventListener('mousemove', resetActivityTimeout);
    window.addEventListener('keydown', resetActivityTimeout);
    window.addEventListener('click', resetActivityTimeout);
 
    checkAndRefreshToken();
    resetActivityTimeout();
 
    return () => {
      clearInterval(interval);
      clearTimeout(activityTimeout);
      window.removeEventListener('mousemove', resetActivityTimeout);
      window.removeEventListener('keydown', resetActivityTimeout);
      window.removeEventListener('click', resetActivityTimeout);
    };
  }, [navigate]);
 
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isValidUsername = (value) => /^[A-Za-z0-9]+$/.test(value);
 
  // Step 1: Submit Email/Password
 const handleCredentialsSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setLoading(true);
 
  if (!loginInput || !password) {
    setError('Both fields are required.');
    setLoading(false);
    return;
  }
 
  if (!isValidEmail(loginInput) && !isValidUsername(loginInput)) {
    setError('Please enter a valid email or username.');
    setLoading(false);
    return;
  }
 
  try {
    await loginUser(loginInput, password);
    setEmailForOtp(loginInput);
    setStep(2);
    setTimeLeft(300);
     setResendEnabled(false);
      toast.success('OTP has been sent to your registered email.');
  } catch (err) {
    if (err.message.startsWith('ServiceDown:')) {
      const service = err.message.split(':')[1];
      navigate(`/service-down/${service}`);
    } else {
      setError(err.message || 'Failed to send OTP. Please try again.');
     // toast.error(err.message || 'Failed to send OTP. Please try again.');
    }
  } finally {
      setLoading(false);
    }
};
 
 useEffect(() => {
    if (step === 2 && timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0) {
      setResendEnabled(true);
    }
  }, [step, timeLeft]);
 
 
  // Step 2: Submit OTP
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
  setLoading(true);
  setLoadingText('Verifying OTP...');
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP.');
       setLoading(false);
      return;
    }
 
    try {
      const { accessToken, refreshToken, role } = await verifyOtp(emailForOtp, otp);
 
      const decodedToken = jwtDecode(accessToken);
      const username = decodedToken.sub?.split('@')[0] || 'User';
 
      localStorage.setItem('bgc_token', accessToken);
      localStorage.setItem('bgc_refresh_token', refreshToken);
      localStorage.setItem('user_email', emailForOtp);
      localStorage.setItem('user_role', role);
      localStorage.setItem('user_name', username);
 
      // Redirect based on role
      if (role === 'USER') navigate('/client');
      else if (role === 'EMPLOYEE') navigate('/employee');
      else if (role === 'ADMIN') navigate('/admin');
      else if (role === 'CANDIDATE') fetchBasicInfo();
      else navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP.');
    }finally {
    setLoading(false);
  }
  };
 
 
 
  const handleResendOtp = async () => {
    setError('');
    setLoading(true);
    setLoadingText('Resending OTP...');
    try {
      await loginUser(emailForOtp, password);
      setTimeLeft(300);
      setResendEnabled(false);
    } catch {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
      setLoadingText('');
    }
  };
 
  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };
 
  return (
    <>
   {loading && (
        <div className="loader-overlay">
          <div className="spinner"></div><br></br>
          {/* <p >{loadingText || 'Processing...'}</p> */}
                  {/* <h3 className="loader-text">OTP Sending ... </h3> */}

        </div>
      )}
 
    <div className="login-wrapper">
      <div className="login-form-container">
        <div style={{ width: '100%', maxWidth: '400px' }}>
         {step === 1 ? (
            <>
              <h2 className="login-title">Welcome Back</h2>
              <p className="login-subtitle">Sign in to continue</p>
            </>
          ) : (
           <>
  <div className="otp-header">
    <h2 className="otp-title">OTP Verification</h2>
    <div className="otp-divider"></div>
    <p className="otp-subtitle">
      We’ve sent a 6-digit verification code to your registered email.
    </p>
  </div>
</>
 
          )}
 
 
 
          {/* Step 1: Email & Password */}
          {step === 1 && (
            <form onSubmit={handleCredentialsSubmit}>
              <div className="input-group">
                <label className="input-label">Email or Username</label>
                <div className="input-wrapper">
                  <FiMail className="input-icon" />
                  <input
                    type="text"
                    value={loginInput}
                    onChange={(e) => setLoginInput(e.target.value)}
                    className="input-field"
                    placeholder="Enter email or username"
                    required
                  />
                </div>
              </div>
 
              <div className="input-group">
                <label className="input-label">Password</label>
                <div className="input-wrapper">
                  <FiLock className="input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field"
                    placeholder="Password"
                    required
                  />
                  {showPassword ? (
                    <FiEyeOff
                      className="password-toggle"
                      onClick={() => setShowPassword(false)}
                    />
                  ) : (
                    <FiEye
                      className="password-toggle"
                      onClick={() => setShowPassword(true)}
                    />
                  )}
                </div>
              </div>
 
              <div className="extra-options">
                <label className="remember-me">
                <input type="checkbox" className="checkbox" />
                 Remember me
               </label>

              <span
                className="forgot-password"
                onClick={() => navigate("/forgot-password")}
                style={{ cursor: "pointer" }}
                 >
                Forgot password?
              </span>
              </div>
              {error && <div className="error-message">{error}</div>}
              <button type="submit" className="login-button">
                Send OTP
              </button>
            </form>
          )}
 
       
        </div>
          {step === 2 && (
            <form onSubmit={handleOtpSubmit}>
              <div className="input-group">
                <label className="input-label">One-Time Password</label>
                <div className="input-wrapper">
                  <FiSmartphone className="input-icon" />
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input-field"
                    placeholder="Enter 6-digit OTP"
                    maxLength="6"
                    required
                    autoFocus
                  />
                </div>
                {/* <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' ,marginbottom: '20px ' }}>
                  Check your email: <strong>{emailForOtp}</strong>
                </p> */}
              </div>
              <p
                style={{
                  fontSize: '1rem',
                  color: resendEnabled ? '#b00' : '#555',
                  marginTop: '0px',
                      float: 'right',
                  marginBottom: '24px'
                }}
              >
                {resendEnabled ? (
                  <>
                    OTP expired.{' '}
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="resend-btn"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#4D0054',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      Resend OTP
                    </button>
                  </>
                ) : (
                  <>OTP expires in  : <strong>{formatTime(timeLeft)}</strong></>
                )}
              </p>
              {error && <div className="error-message">{error}</div>}
 
              <button type="submit" className="login-button">
                Verify OTP & Login
              </button>
 
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setOtp('');
                  setError('');
                }}
                className="login-button"
                style={{ background: 'transparent', color: '#007bff', marginTop: '10px' }}
              >
                Back to Login
              </button>
            </form>
          )}
 
          {step === 1 && (
            <div className="login_registerLink">
              <span>Don't have an account? </span>
              <a onClick={() => navigate('/Registration')}>Sign up</a>
            </div>
          )}
 
 
      </div>
    </div>
    </>
  );
};
 
export default Login;